import { useState, useContext, createContext, useEffect } from "react";
import firebase from "firebase/compat/app";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../config";
import { User } from "firebase/auth";
import { IPatient } from "../types/patient";
import { useHistory } from "react-router-dom";
import amplitude from "amplitude-js";

type UserContextType = {
  patientData?: IPatient;
  authData?: User | null;
  isLoading: boolean;
  logout: () => void;
};

const UserContext = createContext<UserContextType | null>(null);

const firestore = firebase.firestore;

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const history = useHistory<any>();
  const [patientData, setPatientData] = useState<IPatient>();
  const [isPatientDataLoading, setPatientDataLoading] = useState(false);

  const [authData, isAuthLoading] = useAuthState(auth);

  useEffect(() => {
    if (authData?.uid) {
      setPatientDataLoading(true);
      const unsubscribe = firestore()
        .collection("patients")
        .doc(authData?.uid)
        .onSnapshot(
          (documentSnapshot) => {
            if (documentSnapshot.exists) {
              const user = documentSnapshot.data();
              setPatientData(user as IPatient);
            }
            setPatientDataLoading(false);
          },
          (error) => {
            setPatientDataLoading(false);
          }
        );

      return () => unsubscribe();
    }
  }, [authData?.uid]);

  const logout = async () => {
    amplitude.getInstance().setUserId(null);

    // @ts-ignore
    Kustomer.logout((_callbackResponse, _error) => {
      // @ts-ignore
      Kustomer.stop();
    });
    setPatientData(undefined);
    await firebase.auth().signOut();
    history.push({ pathname: "/login", search: window.location.search });
  };

  return (
    <UserContext.Provider
      value={{
        patientData,
        authData,
        isLoading: isPatientDataLoading || isAuthLoading,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("Error from UserProvider context api");
  }

  return context;
};
